// src/pages/customers/EditCustomerPage.tsx
import { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { FiClock } from "react-icons/fi";

import AdminLayout from "@/layouts/AdminLayout";
import Breadcrumbs from "@/components/shared/nav/Breadcrumbs";
import Field from "@/components/shared/ui/Field";
import Select from "@/components/shared/ui/Select";

import EditCustomerForm, {
  type Customer as UiCustomer,
} from "@/components/customers/forms/EditCustomerForm";

import { useCustomer } from "@/api/features/customers/queries";
import {
  useCustomerWallet,
  useCustomerWalletTransactions,
} from "@/api/features/customers/queries";
import {
  useCreateCustomerWalletTransactionMutation,
  useToggleCustomerWalletMutation,
  useToggleCustomerHoldBookingMutation,
} from "@/api/features/customers/mutations";
import { useCustomersPage } from "./useCustomersPage";
import type { CustomerResult } from "@/api/features/customers/types";

const HOLD_BOOKING_HINT =
  "Disabled → Customer must pay in full to confirm a booking. " +
  "Enabled → Customer may hold a seat now and pay later. Holding blocks inventory " +
  "at the airline without payment, and a hold that lapses unticketed may attract a " +
  "penalty. Whether a specific fare can be held is still decided by the airline and " +
  "supplier at checkout. Changes are recorded in the audit log.";

/** Pull the server's message off an axios-style error, falling back cleanly. */
function readApiErrorMessage(err: unknown, fallback: string): string {
  const res = (err as { response?: { data?: { message?: unknown } } })?.response;
  if (typeof res?.data?.message === "string" && res.data.message.trim()) {
    return res.data.message;
  }
  const msg = (err as { message?: unknown })?.message;
  return typeof msg === "string" && msg.trim() ? msg : fallback;
}

/* Helper: map API CustomerResult → EditCustomerForm UI type */
function mapApiToUiCustomer(c: CustomerResult): UiCustomer {
  return {
    id: c.id,
    firstName: c.firstName,
    lastName: c.lastName,
    username: c.username,

    email: c.email,
    phone: c.phone ?? "",

    status: c.status, // "ACTIVE" | "INACTIVE" | "BLOCKED"

    // Profile
    gender: (c.gender as any) ?? undefined,
    dob: c.dob ? c.dob.slice(0, 10) : "", // ISO → yyyy-mm-dd
    idType: (c.idType as any) ?? undefined,
    idNumber: c.idNumber ?? "",

    // Address
    addressLine1: c.addressLine1 ?? "",
    addressLine2: c.addressLine2 ?? "",
    country: c.country ?? "",
    state: c.state ?? "",
    city: c.city ?? "",
  };
}

export default function EditCustomerPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const customerId = Number(id || 0);

  // Centralized mutations / errors from existing hook
  const page = useCustomersPage();

  // Fetch full customer detail from API
  const {
    data: customerResult,
    isLoading,
    isError,
    error,
  } = useCustomer(id ?? null);
  const walletQ = useCustomerWallet(id ?? null);
  const walletTxnQ = useCustomerWalletTransactions(id ?? null, {
    page: 1,
    limit: 20,
  });
  const toggleWalletMut = useToggleCustomerWalletMutation();
  const toggleHoldMut = useToggleCustomerHoldBookingMutation();
  const createTxnMut = useCreateCustomerWalletTransactionMutation();
  const [openingBalance, setOpeningBalance] = useState("0");
  const [walletTxnType, setWalletTxnType] = useState<"CREDIT" | "DEBIT">("CREDIT");
  const [walletTxnAmount, setWalletTxnAmount] = useState("");
  const [walletTxnNote, setWalletTxnNote] = useState("");
  const [walletTxnProductType, setWalletTxnProductType] = useState("OTHER");
  const [walletTxnBookingId, setWalletTxnBookingId] = useState("");
  const [showAddTransaction, setShowAddTransaction] = useState(false);
  const [pageTab, setPageTab] = useState<"overview" | "wallet" | "bookings">(
    "overview"
  );

  const uiCustomer: UiCustomer | null = useMemo(
    () => (customerResult ? mapApiToUiCustomer(customerResult) : null),
    [customerResult]
  );

  const handleSubmit = (payload: UiCustomer) => {
    page.onEdit(payload, {
      onSuccess: () => {
        navigate("/customers");
      },
    });
  };

  const handleCancel = () => {
    navigate("/customers");
  };

  const errorMessage =
    page.updateError ||
    (isError ? (error as any)?.message ?? "Failed to load customer." : null);

  const walletBalance = Number(walletQ.data?.balance ?? customerResult?.walletBalance ?? 0);
  const walletCurrency = String(walletQ.data?.currency || customerResult?.walletCurrency || "INR");
  const walletEnabled = Boolean(walletQ.data?.enabled ?? customerResult?.walletEnabled);

  // Hold rides on the customer detail payload; the mutation invalidates that
  // query, so this reflects server truth rather than local optimism.
  const holdEnabled = Boolean(
    toggleHoldMut.data?.enabled ?? customerResult?.holdBookingEnabled
  );

  // The dropdown edits a DRAFT; nothing is granted until Save is pressed. The
  // draft starts unset so it cannot fight the server value while the customer
  // is still loading — `?? holdEnabled` resolves it once that lands.
  const [holdDraft, setHoldDraft] = useState<boolean | null>(null);
  const holdSelected = holdDraft ?? holdEnabled;
  const holdDirty = holdSelected !== holdEnabled;
  // Prefer the API's own message ("Customer not found", a guard rejection) over
  // the transport's, so the admin is told why the grant was refused.
  const holdError = toggleHoldMut.isError
    ? readApiErrorMessage(toggleHoldMut.error, "Failed to update hold permission.")
    : null;

  return (
    <AdminLayout>
      {/* Header */}
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">
            Edit Customer
          </h1>
          <div className="mt-1">
            <Breadcrumbs />
          </div>
        </div>
      </div>

      {/* Content card */}
      <div className="rounded-2xl border border-gray-200 bg-white p-4 sm:p-6">
        {isLoading && (
          <div className="text-sm text-gray-500">Loading customer…</div>
        )}

        {!isLoading && isError && (
          <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            {errorMessage}
          </div>
        )}

        {!isLoading && !isError && uiCustomer && (
          <>
            <div className="mb-4 inline-flex rounded-lg border border-gray-200 bg-slate-50 p-1 text-sm font-medium">
              <button
                type="button"
                onClick={() => setPageTab("overview")}
                className={`rounded-md px-4 py-2 ${
                  pageTab === "overview" ? "bg-sky-600 text-white" : "text-gray-700"
                }`}
              >
                Overview
              </button>
              <button
                type="button"
                onClick={() => setPageTab("wallet")}
                className={`rounded-md px-4 py-2 ${
                  pageTab === "wallet" ? "bg-sky-600 text-white" : "text-gray-700"
                }`}
              >
                Wallet
              </button>
              <button
                type="button"
                onClick={() => setPageTab("bookings")}
                className={`rounded-md px-4 py-2 ${
                  pageTab === "bookings" ? "bg-sky-600 text-white" : "text-gray-700"
                }`}
              >
                Bookings
              </button>
            </div>

            {pageTab === "overview" ? (
              <EditCustomerForm
                customer={uiCustomer}
                onSubmit={handleSubmit}
                submitting={page.updateSubmitting}
                onCancel={handleCancel}
                errorMessage={errorMessage}
              />
            ) : null}

            {pageTab === "wallet" ? (
              <div className="rounded-xl border border-gray-200 bg-slate-50 p-4">
                <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                  <h2 className="text-sm font-semibold text-gray-900">Customer Wallet</h2>
                  <div className="text-xs text-gray-600 rounded-md bg-white border border-gray-200 px-2 py-1">
                    Balance: <span className="font-semibold text-gray-900">{walletCurrency} {Number.isFinite(walletBalance) ? walletBalance.toFixed(2) : "0.00"}</span>
                  </div>
                </div>

                <div className="rounded-lg border border-gray-200 bg-white p-3">
                  <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-600">Overview</div>
                  <div className="grid gap-3 md:grid-cols-[1fr_160px_auto]">
                  <label className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700">
                    <input
                      type="checkbox"
                      checked={walletEnabled}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        toggleWalletMut.mutate({
                          id: customerId,
                          data: {
                            enabled: checked,
                            openingBalance: checked ? Number(openingBalance || 0) : undefined,
                            note: checked ? "Wallet enabled by admin" : "Wallet disabled by admin",
                          },
                        });
                      }}
                      disabled={toggleWalletMut.isPending || !customerId}
                    />
                    Enable wallet
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={openingBalance}
                    onChange={(e) => setOpeningBalance(e.target.value)}
                    placeholder="Opening amount"
                    className="h-10 rounded-lg border border-gray-300 px-3 text-sm"
                  />
                  <button
                    type="button"
                    onClick={() =>
                      toggleWalletMut.mutate({
                        id: customerId,
                        data: {
                          enabled: true,
                          openingBalance: Number(openingBalance || 0),
                          note: "Wallet enabled/set by admin",
                        },
                      })
                    }
                    disabled={toggleWalletMut.isPending || !customerId}
                    className="h-10 rounded-lg border border-sky-600 bg-white px-3 text-sm font-semibold text-sky-700 disabled:opacity-60"
                  >
                    {toggleWalletMut.isPending ? "Saving..." : "Set Opening Balance"}
                  </button>
                </div>
                </div>

                <div className="mt-4 overflow-hidden rounded-lg border border-gray-200 bg-white">
                  <div className="border-b border-gray-200 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-gray-600">
                    Wallet Transaction History
                  </div>
                  <div className="max-h-72 overflow-auto">
                    <div className="min-w-[920px]">
                      <table className="w-full text-left text-xs">
                        <thead className="sticky top-0 bg-gray-50 text-gray-600">
                          <tr>
                            <th className="px-3 py-2">Date</th>
                            <th className="px-3 py-2">Type</th>
                            <th className="px-3 py-2">Amount</th>
                            <th className="px-3 py-2">Before</th>
                            <th className="px-3 py-2">After</th>
                            <th className="px-3 py-2">Product</th>
                            <th className="px-3 py-2">Booking</th>
                            <th className="px-3 py-2">Modified By</th>
                            <th className="px-3 py-2">Note</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(walletTxnQ.data?.items || []).map((row) => (
                            <tr key={row.id} className="border-t border-gray-100">
                              <td className="px-3 py-2 text-gray-700">
                                {row.createdAt ? new Date(row.createdAt).toLocaleString() : "--"}
                              </td>
                              <td className="px-3 py-2 font-semibold text-gray-800">{row.txnType}</td>
                              <td className="px-3 py-2 text-gray-700">{row.currency} {Number(row.amount || 0).toFixed(2)}</td>
                              <td className="px-3 py-2 text-gray-600">{row.currency} {Number(row.balanceBefore || 0).toFixed(2)}</td>
                              <td className="px-3 py-2 text-gray-900">{row.currency} {Number(row.balanceAfter || 0).toFixed(2)}</td>
                              <td className="px-3 py-2 text-gray-700">{row.productType || "--"}</td>
                              <td className="px-3 py-2 text-gray-700">{row.bookingId ?? "--"}</td>
                              <td className="px-3 py-2 text-gray-700">
                                {row.createdByName || row.createdBy || "--"}
                                {row.createdByUserId ? ` (#${row.createdByUserId})` : ""}
                              </td>
                              <td className="px-3 py-2 text-gray-600">{row.note || "--"}</td>
                            </tr>
                          ))}
                          {!walletTxnQ.isLoading && (walletTxnQ.data?.items || []).length === 0 ? (
                            <tr>
                              <td colSpan={9} className="px-3 py-6 text-center text-xs text-gray-500">
                                No wallet transactions yet.
                              </td>
                            </tr>
                          ) : null}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>

                <div className="mt-4 flex justify-end">
                  <button
                    type="button"
                    onClick={() => setShowAddTransaction(prev => !prev)}
                    className="h-10 rounded-lg bg-sky-600 px-4 text-sm font-semibold text-white"
                  >
                    {showAddTransaction ? "Hide Transaction Form" : "Add Transaction"}
                  </button>
                </div>

                {showAddTransaction ? (
                  <div className="mt-3 rounded-lg border border-sky-200 bg-sky-50/60 p-3">
                    <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-sky-800">
                      Add Wallet Transaction
                    </div>
                    <div className="grid gap-3 md:grid-cols-[140px_160px_1fr_auto]">
                      <select
                        value={walletTxnType}
                        onChange={(e) => setWalletTxnType(e.target.value as "CREDIT" | "DEBIT")}
                        className="h-10 rounded-lg border border-gray-300 px-3 text-sm"
                      >
                        <option value="CREDIT">Credit</option>
                        <option value="DEBIT">Debit</option>
                      </select>
                      <input
                        type="number"
                        min="0.01"
                        step="0.01"
                        value={walletTxnAmount}
                        onChange={(e) => setWalletTxnAmount(e.target.value)}
                        placeholder="Amount"
                        className="h-10 rounded-lg border border-gray-300 px-3 text-sm"
                      />
                      <input
                        value={walletTxnNote}
                        onChange={(e) => setWalletTxnNote(e.target.value)}
                        placeholder="Note (optional)"
                        className="h-10 rounded-lg border border-gray-300 px-3 text-sm"
                      />
                      <button
                        type="button"
                        onClick={() =>
                          createTxnMut.mutate(
                            {
                              id: customerId,
                              data: {
                                txnType: walletTxnType,
                                amount: Number(walletTxnAmount || 0),
                                note: walletTxnNote || null,
                                referenceType: "ADMIN_MANUAL",
                                referenceId: String(customerId),
                                productType: walletTxnProductType || "OTHER",
                                bookingId: walletTxnBookingId ? Number(walletTxnBookingId) : null,
                              },
                            },
                            {
                              onSuccess: () => {
                                setWalletTxnAmount("");
                                setWalletTxnNote("");
                                setWalletTxnBookingId("");
                                setShowAddTransaction(false);
                              },
                            }
                          )
                        }
                        disabled={
                          !walletEnabled ||
                          createTxnMut.isPending ||
                          !customerId ||
                          !(Number(walletTxnAmount) > 0)
                        }
                        className="h-10 rounded-lg bg-sky-600 px-3 text-sm font-semibold text-white disabled:opacity-60"
                      >
                        {createTxnMut.isPending ? "Saving..." : "Save"}
                      </button>
                    </div>
                    <div className="mt-3 grid gap-3 md:grid-cols-2">
                      <select
                        value={walletTxnProductType}
                        onChange={(e) => setWalletTxnProductType(e.target.value)}
                        className="h-10 rounded-lg border border-gray-300 px-3 text-sm"
                      >
                        <option value="TOUR">Tour</option>
                        <option value="FLIGHT">Flight</option>
                        <option value="HOTEL">Hotel</option>
                        <option value="ACTIVITY">Activity</option>
                        <option value="VISA">Visa</option>
                        <option value="INSURANCE">Insurance</option>
                        <option value="TRANSPORT">Transport</option>
                        <option value="OTHER">Other</option>
                      </select>
                      <input
                        type="number"
                        min="1"
                        step="1"
                        value={walletTxnBookingId}
                        onChange={(e) => setWalletTxnBookingId(e.target.value)}
                        placeholder="Booking ID (optional)"
                        className="h-10 rounded-lg border border-gray-300 px-3 text-sm"
                      />
                    </div>
                  </div>
                ) : null}
              </div>
            ) : null}

            {pageTab === "bookings" ? (
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div className="sm:col-span-2">
                  <Field label="Hold Booking" icon={<FiClock />} hint={HOLD_BOOKING_HINT}>
                    <Select
                      value={holdSelected ? "Enabled" : "Disabled"}
                      onChange={(e) => setHoldDraft(e.target.value === "Enabled")}
                      options={["Disabled", "Enabled"]}
                      disabled={toggleHoldMut.isPending || !customerId}
                    />
                  </Field>

                  {holdError ? (
                    <div className="mt-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                      {holdError}
                    </div>
                  ) : null}

                  {toggleHoldMut.isSuccess && !holdDirty ? (
                    <div className="mt-3 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-700">
                      Hold booking {holdEnabled ? "enabled" : "disabled"} for this
                      customer.
                    </div>
                  ) : null}
                </div>

                {/* Actions — mirrors the Overview form footer. */}
                <div className="mt-2 flex justify-end gap-2 sm:col-span-2">
                  <button
                    type="button"
                    className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-60"
                    onClick={() => setHoldDraft(null)}
                    disabled={!holdDirty || toggleHoldMut.isPending}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="rounded-lg bg-sky-600 px-3 py-2 text-sm font-semibold text-white hover:bg-sky-700 disabled:opacity-60"
                    onClick={() =>
                      toggleHoldMut.mutate(
                        {
                          id: customerId,
                          data: {
                            enabled: holdSelected,
                            note: holdSelected
                              ? "Hold booking enabled by admin"
                              : "Hold booking disabled by admin",
                          },
                        },
                        // Drop the draft so the field falls back to whatever the
                        // server confirms, rather than showing an unsaved edit.
                        { onSuccess: () => setHoldDraft(null) }
                      )
                    }
                    disabled={!holdDirty || toggleHoldMut.isPending || !customerId}
                  >
                    {toggleHoldMut.isPending ? "Saving…" : "Save Changes"}
                  </button>
                </div>
              </div>
            ) : null}
          </>
        )}
        {!isLoading && !isError && !uiCustomer && (
          <div className="text-sm text-gray-500">
            Customer not found or has been deleted.
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
